import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { useAppModule } from '../features/appContext.ts'
import { Subscription } from '../shared/api/chatApi.ts'
import { urls } from '../shared/urls.ts'
import { Button } from '../shared/ui/button/button.tsx'
import { Spinner } from '../shared/ui/spinner.tsx'

export function PaymentStatusPage(props: { status?: 'success' | 'error' }) {
  const { chatsApi, authStore } = useAppModule()
  const params = useParams<{ status: string; plan: string }>()
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const success = (props.status ?? params.status) === 'success'
  const subscriptionId = search.get('subscription_id')
  const plan = params.plan?.toUpperCase()
  const [subscription, setSubscription] = useState<Subscription>()
  const [loading, setLoading] = useState(success)
  const [error, setError] = useState(false)
  const [retry, setRetry] = useState(0)
  const trackedSubscription = useRef<number>()

  useEffect(() => {
    if (!success) return
    let cancelled = false
    setLoading(true)
    setError(false)
    setSubscription(undefined)
    const load = async () => {
      if (subscriptionId !== null) {
        const id = Number(subscriptionId)
        if (!Number.isSafeInteger(id) || id <= 0)
          throw new Error('Invalid subscription')
        return chatsApi.subscription(id)
      }
      // Older return URLs have no ID. Use a confirmed subscription matching the plan.
      const subscriptions = await chatsApi.subscriptionsList()
      return subscriptions
        .filter(
          (item) =>
            (!plan || item.plan === plan) &&
            (item.status === 'trial' || item.status === 'active'),
        )
        .sort((a, b) => b.id - a.id)[0]
    }
    void load().then(
      (result) => {
        if (!cancelled) {
          setSubscription(result)
          setLoading(false)
        }
      },
      () => {
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [chatsApi, success, subscriptionId, plan, retry])

  const confirmed =
    subscription?.status === 'trial' || subscription?.status === 'active'
  const failed =
    !success ||
    (!!subscription &&
      ['failed', 'expired', 'inactive', 'deleted'].includes(
        subscription.status,
      ))
  useEffect(() => {
    if (
      !success ||
      !confirmed ||
      trackedSubscription.current === subscription.id
    )
      return
    trackedSubscription.current = subscription.id
    authStore.setSubscribeState(true)
    authStore.trackMetrika('paing_finished')
    if (subscription.status === 'trial')
      authStore.trackMetrika(`trial_started_${subscription.plan}`)
    authStore.trackMetrika('demo2_successful_payment')
  }, [authStore, success, confirmed, subscription])

  const content = () => {
    if (failed) return <FormattedMessage id="pleaseTryAgain" />
    if (loading)
      return (
        <p role="status">
          <FormattedMessage id="billing.loading" />
        </p>
      )
    if (error)
      return (
        <p role="alert">
          <FormattedMessage id="billing.accessCheckError" />
        </p>
      )
    if (!confirmed)
      return (
        <p role="status">
          <FormattedMessage id="billing.pending" />
        </p>
      )
    const amount = subscription.billing?.amount ?? subscription.amount
    const currency = subscription.billing?.currency ?? 'RUB'
    if (!subscription.recurrent) {
      return (
        <FormattedMessage
          id="payment.renewalDisabled"
          values={{ date: subscription.end }}
        />
      )
    }
    return (
      <>
        <p>
          <FormattedMessage
            id={
              subscription.status === 'trial'
                ? 'payment.trialSuccess'
                : 'payment.paidSuccess'
            }
            values={{
              days: subscription.billing?.trial_days ?? subscription.days,
              amount,
              currency,
            }}
          />
        </p>
        {subscription.end && (
          <p className="mt-16">
            <FormattedMessage
              id="payment.nextCharge"
              values={{ date: subscription.end }}
            />
          </p>
        )}
        <p className="mt-16">
          <FormattedMessage id="payment.manageRenewal" />
        </p>
      </>
    )
  }
  const title =
    failed || error
      ? 'payment.error'
      : confirmed && !loading
      ? 'payment.success'
      : 'payment.confirming'

  return (
    <section className="mx-auto flex min-h-dvh w-full max-w-800 flex-col items-center justify-center gap-24 px-16 py-32 text-center">
      <PaymentStatusIcon
        confirmed={confirmed}
        failed={failed}
        loading={loading}
        error={error}
      />
      <h1 className="text-24 font-extrabold sm:text-32">
        <FormattedMessage id={title} />
      </h1>
      <div className="max-w-[560px] text-18">{content()}</div>
      {success && confirmed && !loading ? (
        <Button
          bg="blue-gradient"
          size="large"
          rounded="full"
          className="w-full max-w-400"
          onClick={() => {
            navigate(urls.main)
          }}
        >
          <FormattedMessage id="payment.openService" />
        </Button>
      ) : failed ? (
        <Button
          bg="blue-gradient"
          size="large"
          rounded="full"
          onClick={() => {
            navigate(urls.subscriptions())
          }}
        >
          <FormattedMessage id="payment.return" />
        </Button>
      ) : (
        <Button
          bg="blue-gradient"
          size="large"
          rounded="full"
          disabled={loading}
          onClick={() => {
            setRetry((value) => value + 1)
          }}
        >
          <FormattedMessage id="billing.retry" />
        </Button>
      )}
      <Link to={urls.profile()} className="text-blue4 underline">
        <FormattedMessage id="billing.account" />
      </Link>
    </section>
  )
}

function PaymentStatusIcon(props: {
  confirmed: boolean
  failed: boolean
  loading: boolean
  error: boolean
}) {
  if (props.loading || (!props.confirmed && !props.failed && !props.error)) {
    return <Spinner size="large" className="my-72 text-blue4" />
  }
  return (
    <img
      src={
        props.confirmed
          ? '/images/status-success.png'
          : '/images/status-error.png'
      }
      alt=""
      className="size-[180px] sm:size-[228px]"
    />
  )
}
